import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { v4 } from 'uuid';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { Divider } from '@hivebrite/design-system/Divider';
import { CheckboxInput } from '@hivebrite/design-system/Inputs';
import { toast } from '@hivebrite/design-system/Toast';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import ModalWithTemplate from 'hb-react/shared/components/DesignSystem/Modal/ModalWithTemplate';
import dayjs from 'hb-react/shared/utils/dayjs/dayjs';
import * as errorReporting from 'hb-react/shared/utils/errorReporting';

import { commands } from './commands';

const Footer = ({ onClose, onSubmit, onSubmitSuccess, isValid = false }) => {
  const [isLoading, setLoading] = useState(false);

  const submit = async () => {
    try {
      setLoading(true);
      await onSubmit();
      onSubmitSuccess?.();
    } catch (_error) {
      toast.error(__('Sorry, an error occurred'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box data-testid="update-address-popup">
      <Button onClick={onClose} variant="secondary" mr={2}>
        {__('Do not update')}
      </Button>
      <Button onClick={submit} isLoading={isLoading} isDisabled={!isValid}>
        {__('Update selected')}
      </Button>
    </Box>
  );
};

const Header = () => __('You have modified your personal information');

const Label = ({ label }) => (
  <LabelWrapper>
    {__('Update')} {label}
  </LabelWrapper>
);

const defaultRenderer = (data, type) => {
  try {
    if (!data || Object.keys(data).length === 0) {
      return '-';
    }

    const [value] = Object.values(data);

    if (type === 'customizable_attribute_date') {
      return dayjs(value).format('L');
    }

    if (type === 'customizable_attribute_double_select') {
      return `${value.root_value}, ${value.child_value}`;
    }

    if (data instanceof Object) {
      return Object.values(data).join(', ');
    }

    if (Array.isArray(data)) {
      return data.join(', ');
    }

    return data;
  } catch (e) {
    errorReporting.captureException(e);

    return '-';
  }
};

const Data = ({
  id,
  name,
  label,
  selected,
  onToggle,
  payload: { from, to },
  type,
  render = defaultRenderer,
  mt = 0,
}) => {
  const [checked, setChecked] = useState(selected);

  const toggle = () => setChecked((prev) => !prev);

  useEffect(() => {
    onToggle(checked);
  }, [checked]);

  return (
    <Box mt={mt} flexDirection="column">
      <Box mr={2}>
        <CheckboxInput
          id={id}
          name={name}
          checked={checked}
          label={<Label label={label} />}
          onChange={toggle}
        />
      </Box>
      <Divider width="100%" my={3} />
      <Box flexDirection={{ _: 'column', m: 'row' }}>
        <Box flexDirection="column" width={{ _: '100%', m: '50%' }}>
          <Box mb={2}>{s__('AddressChange|From:')}</Box>
          <Box>{render(from, type)}</Box>
        </Box>
        <Box
          flexDirection="column"
          width={{ _: '100%', m: '50%' }}
          mt={{ _: 4, m: 0 }}
        >
          <Box font="body-semibold-asphalt" mb={2}>
            {s__('AddressChange|To:')}
          </Box>
          <Box font="body-semibold-asphalt">{render(to, type)}</Box>
        </Box>
      </Box>
    </Box>
  );
};

const UpdateInformationModal = ({ onClose, onSuccess, changeSet = [] }) => {
  const { currentUser, updateCurrentUser } = useDefaultAppContexts();

  const [data, setData] = useState(() =>
    changeSet.map((element) => ({
      id: v4(),
      selected: true,
      ...element,
    })),
  );

  const toggleData = (id, selected) =>
    setData((data) => {
      const index = data.findIndex((el) => el.id === id);

      const element = data[index];

      return [
        ...data.slice(0, index),
        { ...element, selected },
        ...data.slice(index + 1),
      ];
    });

  const { update } = commands({ currentUser, updateCurrentUser });

  const isValid = data.filter(({ selected }) => selected).length > 0;

  return (
    <ModalWithTemplate
      onCloseModal={() => {
        onClose?.();
      }}
      header={<Header />}
      footer={
        <Footer
          onSubmit={async () => {
            await update(data);
          }}
          onSubmitSuccess={() => {
            onSuccess?.();
          }}
          onClose={onClose}
          isValid={isValid}
        />
      }
      width="medium"
      dataTestId="update-information-modal"
      screenReadersCloseButtonText={s__(
        'NetworkEventsAccessibility|Close update personal information window',
      )}
    >
      <Box flexDirection="column" width="100%">
        <Box>
          {__(
            'Would you like to update your user profile with this new information?',
          )}
        </Box>

        {data.map(({ ...props }, index) => (
          <Data
            key={props.id}
            {...props}
            onToggle={(selected) => toggleData(props.id, selected)}
            mt={index === 0 ? 6 : 8}
          />
        ))}
      </Box>
    </ModalWithTemplate>
  );
};

UpdateInformationModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  changeSet: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      payload: PropTypes.shape({
        from: PropTypes.any.isRequired,
        to: PropTypes.any.isRequired,
      }).isRequired,
      type: PropTypes.string,
      render: PropTypes.func,
    }),
  ).isRequired,
};

export default UpdateInformationModal;

const LabelWrapper = styled(Box).attrs({
  font: 'h3-regular',
  width: '100%',
  as: 'span',
})``;
